/*! Copyright (c) Safe As Milk. All rights reserved. */
import Cart from "cart-store";

class ProductForm extends HTMLElement {
    #boundSubmit;
    #eventAttached;
    static #formsBeingProcessed=[];
    constructor() {
        super();
        this.#boundSubmit = this.submit.bind(this);
    }
    connectedCallback() {
        this.#eventAttached = false;
        setTimeout((() => {
            this.form = this.querySelector("form");
            this.idInput = this.form.querySelector('input[name="id"]');
            this.submitButton = this.form.elements.add;
            this.stagedAction = this.querySelector("staged-action");
            this.idInput.removeAttribute("disabled");
            this.notifyOnAdd = this.hasAttribute("notify-on-add");
            this.redirectOnAdd = this.getAttribute("redirect-on-add");
            this.#eventAttached = true;
            this.form.addEventListener("submit", this.#boundSubmit);
        }));
    }
    disconnectedCallback() {
        if (this.#eventAttached) {
            this.#eventAttached = false;
            this.form.removeEventListener("submit", this.#boundSubmit);
        }
    }
    async submit(e) {
        e.preventDefault();
        const formId = this.#getFormId();
        this.#syncWihapiProperties();
        const data = new FormData(e.target);
        const item = Object.fromEntries(data.entries());
        try {
            this.removeError();
            if (this.redirectOnAdd && !ProductForm.#formsBeingProcessed.includes(formId)) {
                ProductForm.#formsBeingProcessed.push(formId);
            }
            if (this.stagedAction) await this.stagedAction.setState("DOING");
            await Cart.add(item);
            if (this.redirectOnAdd) {
                ProductForm.#formsBeingProcessed = ProductForm.#formsBeingProcessed.filter((id => id !== formId));
                if (ProductForm.#formsBeingProcessed.length === 0) {
                    window.location.href = this.redirectOnAdd;
                }
            }
            if (this.stagedAction) await this.stagedAction.setState("DONE");
            const quickShop = this.closest("quick-shop");
            if (quickShop) {
                await quickShop.close();
            }
            if (this.notifyOnAdd) {
                await Cart.resetVariantsBeingAdded();
                if (!this.closest(".modal--cart")) {
                    const cart = document.querySelector(".modal--cart, .popup--cart-notification");
                    if (cart && !cart.shown) {
                        cart.open();
                    } else if (cart && cart.interval) {
                        cart.interval.reset();
                        cart.interval.start();
                    }
                }
            } else {
                await Cart.resetLatestAddedProduct();
                await Cart.resetVariantsBeingAdded(item.id);
            }
        } catch (error) {
            this.#renderError(error.message);
            if (this.stagedAction) await this.stagedAction.setState("IDLE");
            if (this.redirectOnAdd) {
                ProductForm.#formsBeingProcessed = ProductForm.#formsBeingProcessed.filter((id => id !== formId));
            }
        }
    }
    removeError() {
        const error = this.querySelector(".errors");
        if (!error) return;
        error.remove();
    }
    #renderError(message) {
        this.removeError();
        if (this.submitButton) {
            const error = document.createElement("div");
            error.classList.add("errors", "qty-error", "u-small");
            error.innerHTML = message;
            this.submitButton.before(error);
        }
    }
    #getFormId() {
        return this.form?.getAttribute("id") || "";
    }
    #getCurrentVariant() {
        const variantId = this.idInput?.value;
        if (!variantId) return null;
        const formId = this.#getFormId();
        const uniqueId = formId.replace(/^product-form-installment-/, "").replace(/^product-form-/, "");
        const escapedId = globalThis.CSS?.escape ? CSS.escape(uniqueId) : uniqueId;
        const picker = document.querySelector(`variant-radios[data-card="${escapedId}"], variant-radios[data-section="${escapedId}"], variant-selects[data-card="${escapedId}"], variant-selects[data-section="${escapedId}"], variant-mixed-inputs[data-card="${escapedId}"], variant-mixed-inputs[data-section="${escapedId}"]`);
        if (picker?.currentVariant) {
            return picker.currentVariant;
        }
        const variantJson = picker?.querySelector('[type="application/json"]')?.textContent;
        if (variantJson) {
            try {
                const variants = JSON.parse(variantJson);
                return variants.find((variant => String(variant.id) === String(variantId))) || null;
            } catch {
                return null;
            }
        }
        return null;
    }
    #getVariantSize(variant) {
        if (!variant) return "";
        if (variant.title && variant.title !== "Default Title") {
            return variant.title.split("/")[0].trim();
        }
        if (Array.isArray(variant.options) && variant.options.length > 0) {
            return String(variant.options[0]).trim();
        }
        return "";
    }
    #buildFrameSku({nameXml, size, variant}) {
        if (nameXml && size) {
            return nameXml.replace("MRS-", "MRS_").replace("{poster_size}", size);
        }
        const nativeSku = variant?.sku?.trim();
        if (nativeSku?.startsWith("MRS_")) {
            return nativeSku;
        }
        if (nativeSku?.startsWith("MRS-")) {
            return nativeSku.replace("MRS-", "MRS_");
        }
        return "";
    }
    #syncWihapiProperties() {
        if (!this.form) return;
        const sendToWihapiInput = this.form.querySelector('[name="properties[_wihapi_send_to_wihapi]"]');
        if (!sendToWihapiInput || sendToWihapiInput.value !== "true" && sendToWihapiInput.value !== "True") return;
        const itemTypeInput = this.form.querySelector('[name="properties[_wihapi_item_type]"]');
        const skuInput = this.form.querySelector('[name="properties[_wihapi_sku]"]');
        const paperInput = this.form.querySelector('[name="properties[_wihapi_paper]"]');
        const nameXmlInput = this.form.querySelector('[name="properties[_wihapi_name_xml]"]');
        if (!itemTypeInput || !skuInput) return;
        if (skuInput.value) return;
        const itemType = itemTypeInput.value;
        if (itemType === "book") {
            skuInput.value = "MRS_220x250";
            return;
        }
        if (itemType === "freegift") {
            skuInput.value = "MRS_freegift";
            return;
        }
        if (itemType === "ansichtkaart") {
            skuInput.value = "MRS_ansichtkaart";
            return;
        }
        const sizeInput = this.form.querySelector('[name="properties[_size]"]');
        if (itemType === "poster" && sizeInput?.value) {
            skuInput.value = `MRS_${sizeInput.value}${paperInput?.value ? `_${paperInput.value}` : ""}`;
            return;
        }
        if (itemType === "frame") {
            const variant = this.#getCurrentVariant();
            const size = sizeInput?.value || this.#getVariantSize(variant);
            skuInput.value = this.#buildFrameSku({
                nameXml: nameXmlInput?.value || "",
                size: size,
                variant: variant
            });
            if (size && !sizeInput) {
                const derivedSizeInput = document.createElement("input");
                derivedSizeInput.type = "hidden";
                derivedSizeInput.name = "properties[_size]";
                derivedSizeInput.value = size;
                this.form.appendChild(derivedSizeInput);
            }
            if (skuInput.value) return;
        }
        document.dispatchEvent(new CustomEvent("wihapi:resolve-sku", {
            detail: {
                form: this.form,
                itemType: itemType,
                skuInput: skuInput
            }
        }));
    }
}

if (!customElements.get("product-form")) customElements.define("product-form", ProductForm);
//# sourceMappingURL=product-form.js.map
